import React from 'react';
import { Link, useLocation } from 'wouter';
import { motion } from 'framer-motion';
import { LayoutDashboard, Store, Activity, LogOut, Workflow, Scale, Upload, Globe, Users, MessageSquare, PackageSearch, Disc, RefreshCw, ClipboardList, Layers, Tag, Tags, KeyRound, ShieldCheck, Truck, CreditCard, Car, Wand2, Barcode, Sparkles, Sun, Moon, Database, Building2, ShoppingCart, Bell, Gamepad2, ImagePlus, FileInput, Menu, X } from 'lucide-react';
import { useAuthStore } from '@/lib/auth-store';
import { cn } from '@/lib/utils';
import { useGetMe } from '@workspace/api-client-react';
import { useAuthHeaders } from '@/lib/auth-store';
import { SoraFloMark } from '@/components/soraflo-logo';
import { useTheme } from '@/lib/theme';
import { canUseDynamicWheel } from '@/lib/supplier-access';

const ADMIN_EMAIL = 'marketing@bove.com.au';

interface NavItem {
  href?: string;
  label: string;
  icon: React.ElementType;
  adminOnly?: boolean;
  dynamicWheelOnly?: boolean;
  featureKey?: string;
  flagDefault?: boolean;
  children?: NavItem[];
}

const navGroups: { label: string; items: NavItem[] }[] = [
  {
    label: 'Overview',
    items: [
      { href: '/',         label: 'Dashboard',        icon: LayoutDashboard },
      { href: '/stores',   label: 'Connected Stores',  icon: Store },
      { href: '/copilot',  label: 'SoraFlo AI Agent',  icon: Sparkles, featureKey: 'ai_copilot' },
    ],
  },
  {
    label: 'Automation',
    items: [
      { href: '/flows',                label: 'Automation Flows',     icon: Workflow, featureKey: 'flows' },
      { href: '/customer-automations', label: 'Customer Automations', icon: Users,    featureKey: 'customer_automations' },
      { href: '/logs',                 label: 'Run Logs',             icon: Activity, featureKey: 'logs' },
    ],
  },
  {
    label: 'Sourcing',
    items: [
      { href: '/scraper',     label: 'Product Scraper',  icon: Globe,  featureKey: 'scraper' },
      { href: '/import',      label: 'Product Importer', icon: Upload, featureKey: 'import' },
      { href: '/price-scrape', label: 'Price Scraper',   icon: Tag,    featureKey: 'price_scrape', flagDefault: false },
      {
        label: 'Offroad Imports Sync', icon: Truck, featureKey: 'offroad_imports',
        children: [
          { href: '/offroad-imports/sync',   label: 'Scheduled Sync', icon: RefreshCw },
          { href: '/offroad-imports/import', label: 'Import',         icon: Upload },
        ],
      },
    ],
  },
  {
    label: 'Wheel Pros',
    items: [
      { href: '/wheel-pros',            label: 'Wheel Pros Catalog', icon: Disc,          adminOnly: true, featureKey: 'wheel_pros' },
      { href: '/wheel-pros/sync',       label: 'WP Auto-Sync',       icon: RefreshCw,     featureKey: 'wp_sync' },
      { href: '/wheel-pros/import-log', label: 'WP Import Log',      icon: ClipboardList, adminOnly: true, featureKey: 'wp_import_log' },
    ],
  },
  {
    label: 'Catalogue',
    items: [
      { href: '/product-updates',    label: 'Bulk Product Updater', icon: PackageSearch, featureKey: 'product_updates' },
      { href: '/collections',        label: 'Collections',          icon: Layers,        featureKey: 'collections' },
      { href: '/metafield-update',   label: 'Metafield Update',     icon: Tag,           featureKey: 'metafield_update' },
      { href: '/tag-update',         label: 'Bulk Tag Update',      icon: Tags,          featureKey: 'tag_update' },
      { href: '/product-type-fixer', label: 'Fix Product Types',    icon: Wand2,         featureKey: 'product_type_fixer' },
      { href: '/sku-creator',        label: 'Bulk SKU Creator',     icon: Barcode,       featureKey: 'sku_creator' },
      { href: '/shopify-file-upload',label: 'Shopify File Upload',  icon: ImagePlus,     featureKey: 'shopify_file_upload' },
    ],
  },
  {
    label: 'Fitment Data',
    items: [
      { href: '/fitment', label: 'Vehicle Fitment', icon: Car, featureKey: 'fitment' },
    ],
  },
  {
    label: 'Finance',
    items: [
      { href: '/reconciliation', label: 'Reconciliation', icon: Scale, adminOnly: true, featureKey: 'reconciliation' },
    ],
  },
  {
    label: 'Purchase Orders',
    items: [
      { href: '/po/suppliers',       label: 'Suppliers',        icon: Building2,     featureKey: 'purchase_orders', flagDefault: false },
      { href: '/po/purchase-orders', label: 'Purchase Orders',  icon: ShoppingCart,  featureKey: 'purchase_orders', flagDefault: false },
      { href: '/po/draft-import',    label: 'PDF to Draft Order', icon: FileInput,   featureKey: 'purchase_orders', flagDefault: false },
    ],
  },
  {
    label: 'Shipping',
    items: [
      { href: '/settings/shipping-rates',  label: 'Live Supplier Rates',  icon: Truck,     featureKey: 'supplier_sync',    flagDefault: false },
    ],
  },
  {
    label: 'Inventory',
    items: [
      { href: '/settings/inventory-sync',  label: 'Inventory Sync',       icon: Database,  featureKey: 'inventory_sync',   flagDefault: false },
      { href: '/settings/dynamic-wheel',   label: 'Dynamic Wheel Co API',  icon: Disc,      featureKey: 'inventory_sync',   flagDefault: false, dynamicWheelOnly: true },
      { href: '/notify-me',                label: 'Back-in-Stock Alerts',  icon: Bell,      featureKey: 'notify_me',        flagDefault: false },
      { href: '/game-popup',               label: 'Game Popup',            icon: Gamepad2,  featureKey: 'game_popup',       flagDefault: false },
    ],
  },
  {
    label: 'Settings',
    items: [
      { href: '/settings/billing',         label: 'Billing',              icon: CreditCard },
      { href: '/settings/messaging',       label: 'Messaging',            icon: MessageSquare, adminOnly: true, featureKey: 'messaging' },
      { href: '/settings/wheel-pros',      label: 'WheelPros Account',    icon: KeyRound,      featureKey: 'wheel_pros_settings', flagDefault: false },
    ],
  },
  {
    label: 'Admin',
    items: [
      { href: '/admin', label: 'User Management', icon: ShieldCheck, adminOnly: true },
    ],
  },
];

export function Layout({ children, collapseMobileNav = false }: { children: React.ReactNode; collapseMobileNav?: boolean }) {
  const [location, setLocation] = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);
  React.useEffect(() => { setMobileNavOpen(false); }, [location]);
  const { token, logout } = useAuthStore();
  const authOpts = useAuthHeaders();
  const { theme, toggleTheme } = useTheme();
  const accountQueryScope = React.useMemo(() => crypto.randomUUID(), [token]);

  const { data: user, isLoading: userLoading, isError } = useGetMe({
    request: authOpts,
    query: {
      enabled: !!token,
      retry: false,
      queryKey: ['/api/auth/me', 'layout', accountQueryScope],
      staleTime: 0,
      refetchOnWindowFocus: true,
      refetchInterval: 30_000,
    }
  });

  React.useEffect(() => {
    if (!token || isError) {
      logout();
      setLocation('/auth/login');
    }
  }, [token, isError, logout, setLocation]);

  if (!token) return null;

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-background" onKeyDown={e => { if (e.key === 'Escape') setMobileNavOpen(false); }}>
      {/* Top bar — always dark, same in both modes */}
      <header className="h-[56px] bg-[#0f1117] border-b border-white/[0.06] flex-shrink-0 w-full flex items-center justify-between px-5 z-30">
        <div className="flex items-center gap-3">
          {collapseMobileNav && <button type="button" className="md:hidden text-white p-1" aria-label={mobileNavOpen ? 'Close navigation' : 'Open navigation'} aria-expanded={mobileNavOpen} aria-controls="mobile-responsive-sidebar" onClick={() => setMobileNavOpen(open => !open)}>
            {mobileNavOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>}
          <SoraFloMark size={32} className="text-[#2FE6A8] drop-shadow-[0_0_8px_rgba(47,230,168,0.5)]" />
          <h1 className="text-xl font-display font-bold tracking-tight text-white leading-none">
            Sora<span className="text-muted-foreground font-medium">Flo</span>
          </h1>
        </div>
        <div className={cn("flex items-center", collapseMobileNav ? "gap-2 md:gap-5" : "gap-5")}>
          <div className="flex items-center gap-3">
            <span className={cn("text-muted-foreground text-[13px]", collapseMobileNav && "hidden md:inline-block")}>{user?.email?.toLowerCase()}</span>
            <div className="w-8 h-8 rounded-full bg-muted text-foreground flex items-center justify-center text-[13px] font-medium">
              {user?.email?.charAt(0).toUpperCase() || 'U'}

            </div>
          </div>
          <button
            onClick={toggleTheme}
            className="w-8 h-8 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
        </div>
      </header>

      {/* Main body */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        {collapseMobileNav && mobileNavOpen && <button type="button" className="md:hidden fixed inset-x-0 top-[56px] bottom-0 bg-black/40 z-30" aria-label="Close navigation" onClick={() => setMobileNavOpen(false)} />}
        <aside id={collapseMobileNav ? "mobile-responsive-sidebar" : undefined} className={cn("w-[240px] bg-card border-r border-border flex-col flex-shrink-0",
          collapseMobileNav ? "fixed top-[56px] bottom-0 left-0 z-40 md:static md:z-20 md:flex" : "flex z-20",
          collapseMobileNav && (mobileNavOpen ? "flex" : "hidden"))}>
          <div className="flex-1 overflow-y-auto py-4 space-y-5">
            {userLoading ? (
              Array.from({ length: 9 }).map((_, i) => (
                <div key={i} className="h-7 mx-4 rounded bg-muted animate-pulse" style={{ width: `${55 + (i * 17) % 35}%` }} />
              ))
            ) : navGroups.map((group) => {
              const flags: Record<string, boolean> = (user as any)?.featureFlags ?? {};
              const isAdmin = user?.email === ADMIN_EMAIL || (user as any)?.role === 'admin';
              const visibleItems = group.items.filter(item => {
                if (item.dynamicWheelOnly && !canUseDynamicWheel(user?.email)) return false;
                if (item.href === '/settings/wheel-pros' && isAdmin && !('wheel_pros_settings' in flags)) return true;
                if (item.adminOnly && !isAdmin) return false;
                if (item.featureKey) {
                  if (item.featureKey in flags) return flags[item.featureKey] === true;
                  if (item.flagDefault === false) return false;
                }
                return true;
              });
              if (visibleItems.length === 0) return null;
              
              return (
                <div key={group.label}>
                  <p className="px-4 text-[11px] font-medium text-muted-foreground uppercase tracking-[0.08em] mb-1">
                    {group.label}
                  </p>
                  <nav className="space-y-0.5 px-2">
                    {visibleItems.map((item) => {
                      if (item.children && item.children.length > 0) {
                        const childIsActive = item.children.some(
                          child => child.href && (location === child.href || location.startsWith(child.href))
                        );
                        return (
                          <div key={item.label}>
                            <div
                              className={cn(
                                "flex items-center gap-2.5 px-3 py-2 rounded-[4px] transition-colors cursor-default",
                                childIsActive ? "text-foreground font-medium" : "text-muted-foreground"
                              )}
                            >
                              <item.icon className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
                              <span className="text-[14px]">{item.label}</span>
                            </div>
                            <div className="ml-5 space-y-0.5 border-l border-border pl-2 mt-0.5 mb-1">
                              {item.children.map((child) => {
                                const isActive = !!child.href && (location === child.href || location.startsWith(child.href));
                                return (
                                  <Link
                                    key={child.href}
                                    href={child.href!}
                                    className={cn(
                                      "flex items-center gap-2.5 px-3 py-2 rounded-[4px] transition-colors relative",
                                      isActive
                                        ? "bg-primary/10 text-foreground font-medium"
                                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                                    )}
                                  >
                                    {isActive && (
                                      <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-primary rounded-l-[4px]" />
                                    )}
                                    <child.icon
                                      className={cn(
                                        "w-[14px] h-[14px] flex-shrink-0",
                                        isActive ? "text-primary" : "text-muted-foreground"
                                      )}
                                    />
                                    <span className="text-[14px]">{child.label}</span>
                                  </Link>
                                );
                              })}
                            </div>
                          </div>
                        );
                      }

                      const isActive =
                        location === item.href ||
                        (item.href !== '/' && !!item.href && location.startsWith(item.href));
                      
                      return (
                        <Link
                          key={item.href}
                          href={item.href!}
                          className={cn(
                            "flex items-center gap-2.5 px-3 py-2 rounded-[4px] transition-colors relative",
                            isActive
                              ? "bg-primary/10 text-foreground font-medium"
                              : "text-muted-foreground hover:bg-muted hover:text-foreground"
                          )}
                        >
                          {isActive && (
                            <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-primary rounded-l-[4px]" />
                          )}
                          <item.icon
                            className={cn(
                              "w-4 h-4 flex-shrink-0",
                              isActive ? "text-primary" : "text-muted-foreground"
                            )}
                          />
                          <span className="text-[14px]">{item.label}</span>
                        </Link>
                      );
                    })}
                  </nav>
                </div>
              );
            })}
          </div>

          <div className="flex-shrink-0 p-3 border-t border-border">
            <button
              onClick={() => { logout(); setLocation('/auth/login'); }}
              className="flex items-center gap-2.5 px-3 py-2 w-full rounded-[4px] text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span className="text-[14px]">Log Out</span>
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className={cn("flex-1 flex flex-col h-full overflow-hidden relative", collapseMobileNav && "min-w-0")}>
          <div className={cn("flex-1 overflow-y-auto", collapseMobileNav ? "p-4 md:p-6" : "p-6")}>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className="max-w-6xl mx-auto"
            >
              {children}
            </motion.div>
          </div>
        </main>
      </div>
    </div>
  );
}
